import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ThemeToggle } from './ui/themeToggle';
import { Button } from './ui/Button';
import { LayoutDashboard, ShoppingCart, Menu } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCartStore } from '../store/cart.store';
import { Modal } from './ui/Modal';
import { LoginForm } from './auth/LoginForm';
import { RegisterForm } from './auth/RegisterForm';
import { onOpenAuthModal, type AuthModalMode } from '../lib/authModalEvents';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const cartCount = useCartStore((s) => s.items.reduce((sum, item) => sum + item.quantity, 0));

  // Allow other pages (e.g. the public RepairsPage) to trigger the auth modal.
  useEffect(() => {
    const handleOpenAuth = (mode: AuthModalMode) => {
      if (mode === 'register') {
        setIsLoginModalOpen(false);
        setIsRegisterModalOpen(true);
      } else {
        setIsRegisterModalOpen(false);
        setIsLoginModalOpen(true);
      }
    };
    return onOpenAuthModal(handleOpenAuth);
  }, []);

  const openLogin = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };
  const openRegister = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };
  const closeModals = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-[rgb(var(--border))] bg-[rgb(var(--background))] backdrop-blur-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="EdyTech" className="h-8 w-auto" />
            <span className="text-lg font-bold text-primary">EdyTech</span>
          </Link>

          <nav className="hidden md:flex items-center space-x-6">
            <NavLink to="/repairs" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-primary' : 'hover:text-primary'}`}>Repairs</NavLink>
            <NavLink to="/shop" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-primary' : 'hover:text-primary'}`}>Shop</NavLink>
            <NavLink to="/courses" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-primary' : 'hover:text-primary'}`}>Courses</NavLink>
            <NavLink to="/live-classes" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-primary' : 'hover:text-primary'}`}>Live Classes</NavLink>
            <NavLink to="/about" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-primary' : 'hover:text-primary'}`}>About</NavLink>
            <NavLink to="/contact" className={({ isActive }) => `text-sm font-medium ${isActive ? 'text-primary' : 'hover:text-primary'}`}>Contact</NavLink>
          </nav>

          <div className="flex items-center space-x-2">
            <Link to="/cart" aria-label="Open shopping cart" className="shrink-0">
              <Button variant="ghost" size="sm" className="relative p-2">
                <ShoppingCart size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs text-white">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Button>
            </Link>
            <div className={user ? 'block' : 'hidden md:block'}>
              <ThemeToggle />
            </div>
            {user ? (
              <div className="flex items-center space-x-2">
                <Link to="/dashboard" aria-label="Open dashboard">
                  <Button variant="secondary" size="sm" className="p-2 md:px-3 md:py-1.5">
                    <LayoutDashboard className="h-5 w-5 md:hidden" />
                    <span className="hidden md:inline">Dashboard</span>
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} className="hidden md:inline-flex">Logout</Button>
              </div>
            ) : (
              <div className="flex items-center space-x-1 md:space-x-2">
                <Button variant="secondary" size="sm" onClick={openLogin}>Log In</Button>
                <Button variant="primary" size="sm" onClick={openRegister}>Sign Up</Button>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((open) => !open)}
            >
              <Menu size={24} />
            </Button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="border-t border-[rgb(var(--border))] bg-[rgb(var(--background))] px-4 py-3 md:hidden">
            <nav aria-label="Mobile navigation" className="flex flex-col gap-1">
              {[
                ['Repairs', '/repairs'],
                ['Shop', '/shop'],
                ['Courses', '/courses'],
                ['Live Classes', '/live-classes'],
                ...(user ? [['Dashboard', '/dashboard']] : []),
              ].map(([label, path]) => (
                <NavLink
                  key={path}
                  to={path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 text-sm font-medium ${isActive ? 'bg-primary/10 text-primary' : 'text-[rgb(var(--foreground))] hover:bg-[rgb(var(--border))]'}`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Login Modal */}
      <Modal isOpen={isLoginModalOpen} onClose={closeModals} title="Welcome Back">
        <LoginForm onClose={closeModals} onSwitchToRegister={openRegister} />
      </Modal>

      {/* Register Modal */}
      <Modal isOpen={isRegisterModalOpen} onClose={closeModals} title="Create Account">
        <RegisterForm onClose={closeModals} onSwitchToLogin={openLogin} />
      </Modal>
    </>
  );
};